import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from 'devextreme-react/button';
import DataGrid, {
  Column,
  Editing,
  Form,
  Lookup,
  Pager,
  Paging,
  Popup,
  RequiredRule,
} from 'devextreme-react/data-grid';
import { GroupItem, Item } from 'devextreme-react/form';
import 'devextreme-react/text-area';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import { useAuth } from 'context/auth';
import CustomStore from 'devextreme/data/custom_store';
import $createStore from 'utils/create-store-instance';
import { requestHelper } from 'utils/request-helper';

type Props = {
  selectId: string | number | null;
};

const defaultFilter = ['parentId', '=', null];
const factoryStore = $createStore('/v1/factory', 'id', { defaultFilter });

const DriverSalary: FC<Props> = ({ selectId }) => {
  const { getClaims }: any = useAuth();
  const [factoryState, setFactoryState] = useState<any>([]);

  useEffect(() => {
    factoryStore.load().then(setFactoryState);
  }, []);

  const defaultFilter = useMemo(() => ['objectId', '=', selectId], [selectId]);

  const nomenclaturePriceDataStore: any = useMemo(
    () =>
      new CustomStore({
        load: (loadOptions) =>
          requestHelper('/v1/DriverSalary/configuration', 'get', { ...loadOptions, filter: defaultFilter }),
        insert: async (values) => {
          await requestHelper('/v1/DriverSalary/configuration', 'post', {
            values: values,
          });
        },
        update: async (key, values) =>
          await requestHelper(`/v1/DriverSalary/configuration/${key.id}`, 'put', {
            key: key,
            values: values,
          }),
        remove: async (key) =>
          await requestHelper(`/v1/DriverSalary/configuration/${key.id}`, 'delete', {
            key: key,
          }),
      }),
    [defaultFilter],
  );

  const refGrid = useRef<DataGrid | null>(null);

  const isCanEditDriverTonarSalaryConfiguration = getClaims(['CanEditDriverTonarSalaryConfiguration']);

  return (
    <div className="container-dev">
      <div className="pr-2 pt-1">
        <AppLayoutTop title="">
          <Button
            width={120}
            text="Добавить"
            type="default"
            onClick={() => refGrid.current?.instance.addRow()}
            visible={isCanEditDriverTonarSalaryConfiguration}
          />
        </AppLayoutTop>
      </div>

      <AppCard>
        <DataGrid
          dataSource={nomenclaturePriceDataStore}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          remoteOperations={true}
          onRowUpdating={(e) => (e.newData = { ...e.oldData, ...e.newData })}
          onInitNewRow={(e) =>
            (e.data = { objectId: selectId, factoryId: null, residentSalary: 0, nonResidentSalary: 0 })
          }
        >
          <Editing
            mode="popup"
            allowUpdating={isCanEditDriverTonarSalaryConfiguration}
            texts={{
              editRow: 'Просмотр',
            }}
            allowDeleting={isCanEditDriverTonarSalaryConfiguration}
            //allowDeleting={true}
          >
            <Popup title="Просмотр информации" showTitle={true} width={400} height="auto" />
            <Form>
              <Item itemType="group" colCount={2} colSpan={2}>
                <Item dataField="factoryId" caption="Площадка" colSpan={2}>
                  <RequiredRule message="Заполните поле Площадка" />
                </Item>

                <Item
                  dataField="dateBegin"
                  caption="Дата начала"
                  colSpan={2}
                  editorType="dxDateBox"
                  editorOptions={{
                    format: '###0.##',
                  }}
                >
                  <RequiredRule message="Заполните поле Дата начала" />
                </Item>
                <GroupItem colCount={2} colSpan={2} caption="Резидент">
                  <Item
                    dataField="basicResidentSalary"
                    caption="Базовая ставка резидента"
                    colSpan={2}
                    editorType="dxNumberBox"
                    editorOptions={{
                      format: '###0.##',
                    }}
                  >
                    <RequiredRule message="Заполните поле Базовая ставка резидента" />
                  </Item>
                  <Item
                    dataField="overResidentSalary"
                    caption="Повышенная ставка резидента"
                    colSpan={2}
                    editorType="dxNumberBox"
                    editorOptions={{
                      format: '###0.##',
                    }}
                  >
                    <RequiredRule message="Заполните поле Повышенная ставка резидента" />
                  </Item>
                </GroupItem>

                <GroupItem colCount={2} colSpan={2} caption="Нерезидент">
                  <Item
                    dataField="basicNonResidentSalary"
                    caption="Базовая ставка нерезидента"
                    colSpan={2}
                    editorType="dxNumberBox"
                    editorOptions={{
                      format: '###0.##',
                    }}
                  >
                    <RequiredRule message="Заполните поле Базовая ставка нерезидента" />
                  </Item>
                  <Item
                    dataField="overNonResidentSalary"
                    caption="Повышенная ставка нерезидента"
                    colSpan={2}
                    editorType="dxNumberBox"
                    editorOptions={{
                      format: '###0.##',
                    }}
                  >
                    <RequiredRule message="Заполните поле Повышенная ставка нерезидента" />
                  </Item>
                </GroupItem>
              </Item>
            </Form>
          </Editing>

          <Column dataField="factoryId" caption="Площадка" width={200}>
            <Lookup dataSource={factoryState} valueExpr="id" displayExpr="name" />
          </Column>

          <Column dataField="dateBegin" caption="Дата начала" dataType="date" />
          <Column dataField="dateEnd" caption="Дата окончания" dataType="date" />
          <Column caption="ЗП резидента">
            <Column dataField="basicResidentSalary" caption="Базовая ставка" />
            <Column dataField="overResidentSalary" caption="Повышенная" />
          </Column>
          <Column dataField="nonResidentSalary" caption="ЗП нерезидента" width={120}>
            <Column dataField="basicNonResidentSalary" caption="Базовая ставка" />
            <Column dataField="overNonResidentSalary" caption="Повышенная" />
          </Column>

          <Paging defaultPageSize={15} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default memo(DriverSalary, (prev, next) => {
  return prev.selectId === next.selectId;
});
